import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { writeLog } from '@/hooks/useAdminRole';

interface AdminUser {
  id: string;
  username: string;
  display_name: string;
  role: 'super_admin' | 'editor';
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

interface UserFormData {
  username: string;
  display_name: string;
  role: 'super_admin' | 'editor';
  password: string;
  is_active: boolean;
}

const defaultForm: UserFormData = {
  username: '',
  display_name: '',
  role: 'editor',
  password: '',
  is_active: true,
};

function formatDate(dateStr: string | null) {
  if (!dateStr) return '从未登录';
  const d = new Date(dateStr);
  return d.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function UserManagementPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [form, setForm] = useState<UserFormData>(defaultForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [pwdTarget, setPwdTarget] = useState<AdminUser | null>(null);
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('admin_users')
      .select('id, username, display_name, role, is_active, last_login_at, created_at')
      .order('created_at', { ascending: true });
    if (!error && data) setUsers(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openAdd = () => {
    setEditingUser(null);
    setForm(defaultForm);
    setShowModal(true);
  };

  const openEdit = (user: AdminUser) => {
    setEditingUser(user);
    setForm({
      username: user.username,
      display_name: user.display_name,
      role: user.role,
      password: '',
      is_active: user.is_active,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.username.trim()) { showToast('用户名不能为空', 'error'); return; }
    if (!form.display_name.trim()) { showToast('显示名称不能为空', 'error'); return; }
    if (!editingUser && !form.password.trim()) { showToast('新用户必须设置密码', 'error'); return; }

    setSaving(true);
    if (editingUser) {
      const updateData: Partial<AdminUser & { password_hash?: string; updated_at: string }> = {
        display_name: form.display_name,
        role: form.role,
        is_active: form.is_active,
        updated_at: new Date().toISOString(),
      };
      if (form.password.trim()) {
        (updateData as Record<string, unknown>).password_hash = form.password;
      }
      const { error } = await supabase
        .from('admin_users')
        .update(updateData)
        .eq('id', editingUser.id);
      if (error) { showToast('保存失败：' + error.message, 'error'); }
      else {
        showToast('用户信息已更新');
        await writeLog({ module: '用户管理', action: '编辑', target_name: form.display_name || form.username, detail: `角色: ${form.role === 'super_admin' ? '超级管理员' : '编辑员'}` });
        setShowModal(false); fetchUsers();
      }
    } else {
      const { error } = await supabase.from('admin_users').insert({
        username: form.username,
        password_hash: form.password,
        display_name: form.display_name,
        role: form.role,
        is_active: form.is_active,
      });
      if (error) { showToast('创建失败：' + error.message, 'error'); }
      else {
        showToast('用户已创建');
        await writeLog({ module: '用户管理', action: '新增', target_name: form.display_name || form.username, detail: `用户名: ${form.username}, 角色: ${form.role === 'super_admin' ? '超级管理员' : '编辑员'}` });
        setShowModal(false); fetchUsers();
      }
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    const target = users.find((u) => u.id === id);
    const { error } = await supabase.from('admin_users').delete().eq('id', id);
    if (error) { showToast('删除失败', 'error'); }
    else {
      showToast('用户已删除');
      await writeLog({ module: '用户管理', action: '删除', target_name: target?.display_name || target?.username || id });
      fetchUsers();
    }
    setDeleteConfirm(null);
  };

  const handleToggleActive = async (user: AdminUser) => {
    const { error } = await supabase
      .from('admin_users')
      .update({ is_active: !user.is_active, updated_at: new Date().toISOString() })
      .eq('id', user.id);
    if (!error) {
      const action = user.is_active ? '禁用' : '启用';
      showToast(user.is_active ? '已禁用该账号' : '已启用该账号');
      await writeLog({ module: '用户管理', action, target_name: user.display_name || user.username });
      fetchUsers();
    }
  };

  const openChangePwd = (user: AdminUser) => {
    setPwdTarget(user);
    setNewPwd('');
    setConfirmPwd('');
    setPwdError('');
    setShowPwdModal(true);
  };

  const handleChangePwd = async () => {
    if (!newPwd.trim()) { setPwdError('请输入新密码'); return; }
    if (newPwd.length < 6) { setPwdError('密码至少6位'); return; }
    if (newPwd !== confirmPwd) { setPwdError('两次密码不一致'); return; }
    if (!pwdTarget) return;
    const { error } = await supabase
      .from('admin_users')
      .update({ password_hash: newPwd, updated_at: new Date().toISOString() })
      .eq('id', pwdTarget.id);
    if (error) { setPwdError('修改失败：' + error.message); }
    else {
      showToast('密码已修改');
      await writeLog({ module: '用户管理', action: '修改密码', target_name: pwdTarget.display_name || pwdTarget.username });
      setShowPwdModal(false);
    }
  };

  const roleLabel = (role: string) =>
    role === 'super_admin' ? '超级管理员' : '编辑员';

  const roleColor = (role: string) =>
    role === 'super_admin'
      ? 'bg-amber-100 text-amber-700'
      : 'bg-teal-100 text-teal-700';

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-lg text-sm font-medium shadow-lg transition-all ${
            toast.type === 'success'
              ? 'bg-teal-600 text-white'
              : 'bg-red-500 text-white'
          }`}
        >
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">用户管理</h1>
          <p className="text-sm text-gray-500 mt-0.5">管理后台登录账号与权限</p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-user-add-line"></i>
          </div>
          新增用户
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: '全部账号', value: users.length, icon: 'ri-team-line', color: 'text-gray-700', bg: 'bg-gray-50' },
          { label: '启用中', value: users.filter(u => u.is_active).length, icon: 'ri-checkbox-circle-line', color: 'text-teal-600', bg: 'bg-teal-50' },
          { label: '已禁用', value: users.filter(u => !u.is_active).length, icon: 'ri-forbid-line', color: 'text-red-500', bg: 'bg-red-50' },
        ].map((s) => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4 flex items-center gap-3`}>
            <div className={`w-10 h-10 flex items-center justify-center rounded-lg bg-white ${s.color}`}>
              <i className={`${s.icon} text-xl`}></i>
            </div>
            <div>
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <i className="ri-loader-4-line animate-spin text-2xl mr-2"></i>
            加载中...
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <div className="w-12 h-12 flex items-center justify-center mb-3">
              <i className="ri-user-line text-4xl"></i>
            </div>
            <p className="text-sm">暂无用户数据</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">用户</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">角色</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">状态</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">最后登录</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">创建时间</th>
                <th className="text-right text-xs font-semibold text-gray-500 px-5 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, idx) => (
                <tr
                  key={user.id}
                  className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${
                    idx === users.length - 1 ? 'border-b-0' : ''
                  }`}
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-teal-100 text-teal-700 font-bold text-sm flex-shrink-0">
                        {(user.display_name || user.username).charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.display_name || user.username}</div>
                        <div className="text-xs text-gray-400">@{user.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${roleColor(user.role)}`}>
                      {roleLabel(user.role)}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${user.is_active ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                      {user.is_active ? '启用' : '禁用'}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-500">{formatDate(user.last_login_at)}</td>
                  <td className="px-5 py-4 text-sm text-gray-500">{formatDate(user.created_at)}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openChangePwd(user)}
                        title="修改密码"
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-lock-password-line text-base"></i>
                      </button>
                      <button
                        onClick={() => handleToggleActive(user)}
                        title={user.is_active ? '禁用账号' : '启用账号'}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors cursor-pointer"
                      >
                        <i className={`${user.is_active ? 'ri-forbid-line' : 'ri-checkbox-circle-line'} text-base`}></i>
                      </button>
                      <button
                        onClick={() => openEdit(user)}
                        title="编辑"
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-edit-line text-base"></i>
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(user.id)}
                        title="删除"
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-base"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">
                {editingUser ? '编辑用户' : '新增用户'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">用户名</label>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  disabled={!!editingUser}
                  placeholder="登录用户名"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 disabled:bg-gray-50 disabled:text-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">显示名称</label>
                <input
                  type="text"
                  value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                  placeholder="显示给用户的名称"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">角色</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value as 'super_admin' | 'editor' })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 cursor-pointer"
                >
                  <option value="super_admin">超级管理员</option>
                  <option value="editor">编辑员</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {editingUser ? '新密码（留空则不修改）' : '密码'}
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={editingUser ? '留空则不修改密码' : '请设置登录密码'}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setForm({ ...form, is_active: !form.is_active })}
                  className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                    form.is_active ? 'bg-teal-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      form.is_active ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  ></span>
                </button>
                <span className="text-sm text-gray-700">账号启用</span>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 text-sm bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap"
              >
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPwdModal && pwdTarget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">修改密码</h2>
              <button
                onClick={() => setShowPwdModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-500">
                正在修改 <span className="font-medium text-gray-800">@{pwdTarget.username}</span> 的密码
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">新密码</label>
                <input
                  type="password"
                  value={newPwd}
                  onChange={(e) => { setNewPwd(e.target.value); setPwdError(''); }}
                  placeholder="至少6位"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">确认密码</label>
                <input
                  type="password"
                  value={confirmPwd}
                  onChange={(e) => { setConfirmPwd(e.target.value); setPwdError(''); }}
                  placeholder="再次输入新密码"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
                />
              </div>
              {pwdError && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {pwdError}
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setShowPwdModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleChangePwd}
                className="px-5 py-2 text-sm bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                确认修改
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6">
            <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-2xl"></i>
            </div>
            <h3 className="text-base font-semibold text-gray-900 text-center mb-2">确认删除</h3>
            <p className="text-sm text-gray-500 text-center mb-6">删除后无法恢复，该账号将无法登录后台</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 py-2 text-sm bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
